import { usePermissions } from '@/hooks/usePermissions';
import useTemplateSaveErrorToast from '@/hooks/useTemplateSaveErrorToast';
import { Button, Modal } from '@payaca/ambrosia';
import QuickActionsElement from '@payaca/components/quickActionsTableCell/QuickActionsElement';
import { TemplatesPermissions } from '@payaca/permissions/templates/templates.permissions';
import {
  deleteTemplate,
  updateTemplate,
} from '@payaca/store/templates/templatesActions';
import {
  PublicHydratedEntityTemplate,
  TemplatableEntity,
} from '@payaca/types/entity-templates';
import { FC, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import CreateEditEntityTemplateModal from '../createEditEntityTemplateModal/CreateEditEntityTemplateModal';
import './EntityTemplateViewHeader.sass';

type Props = {
  entityTemplate: PublicHydratedEntityTemplate<TemplatableEntity>;
  onDeleteEntityTemplateSuccess?: () => void;
  onUpdateEntityTemplateSuccess?: (
    updatedEntityTemplate: PublicHydratedEntityTemplate<TemplatableEntity>
  ) => void;
  allowModifyTemplateNameDescription?: boolean;
};
const EntityTemplateViewHeader: FC<Props> = ({
  entityTemplate,
  onDeleteEntityTemplateSuccess,
  onUpdateEntityTemplateSuccess,
  allowModifyTemplateNameDescription,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const onTemplateSaveError = useTemplateSaveErrorToast();
  const [showEditEntityTemplateModal, setShowEditEntityTemplateModal] =
    useState(false);
  const history = useHistory();

  const { userHasRequiredPermission } = usePermissions();

  const [isRemoving, setIsRemoving] = useState(false);

  const [confirmDeleteTemplate, setConfirmDeleteTemplate] = useState(false);

  const quickActions = useMemo(() => {
    const quickActions: any[] = [];

    if (userHasRequiredPermission([TemplatesPermissions.PERSIST_TEMPLATE])) {
      quickActions.push({
        actionName: 'Edit',
        actionBehaviour: () => {
          if (entityTemplate.entityType === 'proposal') {
            history.push(`/templates/edit/${entityTemplate.publicId}`);
          } else {
            setShowEditEntityTemplateModal(true);
          }
        },
      });
    }

    if (
      userHasRequiredPermission([TemplatesPermissions.DELETE_TEMPLATE]) &&
      !entityTemplate.servicePlans?.length
    ) {
      quickActions.push({
        actionName: 'Delete',
        actionBehaviour: () => setConfirmDeleteTemplate(true),
        isActionProcessing: isRemoving,
      });
    }

    return quickActions;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'history', and 'entityTemplate.publicId'
  }, [
    userHasRequiredPermission,
    isRemoving,
    entityTemplate?.servicePlans?.length,
    entityTemplate?.entityType,
  ]);

  const quickActionsElement = useMemo(() => {
    return (
      <QuickActionsElement
        recordId={entityTemplate?.publicId}
        quickActions={quickActions}
      />
    );
  }, [entityTemplate?.publicId, quickActions]);

  if (
    !entityTemplate?.name &&
    entityTemplate?.description &&
    !quickActionsElement
  )
    return null;

  return (
    <div className={'entity-template-view-header'}>
      {entityTemplate?.name && (
        <span className="name">{entityTemplate.name}</span>
      )}
      {quickActionsElement}
      {entityTemplate?.description && (
        <span className="description">{entityTemplate.description}</span>
      )}
      <CreateEditEntityTemplateModal
        isOpen={showEditEntityTemplateModal}
        onClose={() => setShowEditEntityTemplateModal(false)}
        action="Edit"
        template={entityTemplate}
        onRequestSave={(t) => {
          dispatch(
            updateTemplate.request({
              publicId: entityTemplate.publicId,
              template: t,
              callback: (updatedEntityTemplate) => {
                onUpdateEntityTemplateSuccess?.(updatedEntityTemplate);
                setShowEditEntityTemplateModal(false);
              },
              onErrorCallback: onTemplateSaveError,
            })
          );
        }}
        allowModifyTemplateNameDescription={allowModifyTemplateNameDescription}
      />
      <Modal
        isOpen={confirmDeleteTemplate}
        onClose={() => setConfirmDeleteTemplate(false)}
        title="Are you sure you want to delete this template?"
      >
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              onClick={() => {
                setIsRemoving(true);

                dispatch(
                  deleteTemplate.request({
                    publicId: entityTemplate.publicId,
                    callback: () => {
                      onDeleteEntityTemplateSuccess?.();
                      setIsRemoving(false);
                      setConfirmDeleteTemplate(false);
                    },
                    onErrorCallback: (error) => {
                      setIsRemoving(false);
                      setConfirmDeleteTemplate(false);
                    },
                  })
                );
              }}
            >
              Delete
            </Button>
            <Button
              variant="link"
              onClick={() => setConfirmDeleteTemplate(false)}
            >
              Cancel
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
export default EntityTemplateViewHeader;
